import {
    Button,
    Checkbox,
    Collapse,
    Divider,
    Image,
    Input,
    InputNumber,
    Radio,
    Space,
    Switch,
    Typography,
    Upload
} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {compose, mapProps, nest, pure} from 'recompose'
import {ErrorSummaries} from 'views/AppLocals/factory/createEditableForm'
import {deepTranslate, NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import {convertFromString, convertToString} from 'views/SlateEditor/functions'
import {ModalEditorToolbar} from 'views/SlateEditor/SlateEditor'
import FormActionBar from '../../../../components/form/FormActionBar'
import SlateEditor from '../../../SlateEditor'
import {createControlledFormField} from '../../factory/createFormField'
import TextArea from "antd/lib/input/TextArea";
import {CustomToggleField} from "../../../../components/form/fields/ToggleField";
import Pure from "../../../../components/Pure";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {BsImage, BsShieldFillExclamation, BsTrash} from "react-icons/bs";
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {ROOT_URL, STORAGE_URL} from "../../../../envs/_current/config";
import TooltipApp from "../../../../components/TooltipApp";
import {StatusOptions} from "../../CatholicHousehold/consts/StatusOptions";
import {PlusOutlined} from '@ant-design/icons';
import {callApi} from "../../../../modules/asyncCache/callApi";
import {image_directDestroy_Api, image_directUpload_Api} from "../../../../apis";
import {bindQueryParam, bindQueryParams} from "../../functions/routerHelper";
import {useImageActions} from "../../../Workspace/Shop/functions/useImageActions";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import CustomButton from "../../custom/CustomButton";
import classNames from "classnames";
import {renderMaskImage} from "../../../../components/renderFunctions/renderMaskImage";
import ForDevelop from "../../../../envs/ForDevelop";
import {BooleanPicker} from "../../Exam/ExamContext";
import AvatarUploader from "../../../Workspace/components/AvatarUploader";

const {Title, Text} = Typography;


// Định nghĩa cấu trúc dữ liệu mặc định (Initial State)
const getDefaultSettings = (catalogs) => ({
    layout_type: 'BANNER', // Default: BANNER
    photos: [],
    // Trạng thái hiển thị của các mục menu cố định
    menu_visibility: {
        recent: true,
        newest: true,
        discount: true,
        upcoming: false,
    },
    // Cài đặt cho mục "Mới nhất"
    show_newest: true,
    newest_limit: 8, // Mặc định 8 sản phẩm
    // Các danh mục sản phẩm người dùng muốn hiển thị trên Menu (ban đầu lấy từ mock data, mặc định ẩn)
    catalog_menu_items: catalogs.map(cat => ({...cat, isVisible: false})),
});


/**
 * Component cấu hình cài đặt Layout cho Shop
 * @param {object} props
 * @param {object} props.value - Dữ liệu cài đặt hiện tại
 * @param {function} props.onChange - Callback để cập nhật dữ liệu ra ngoài
 */
const ShopLayoutSettings = ({name, value, handleChange = NullAsDefault, catalogs = [], photos = [], shop_id}) => {

    const t = useTranslate()
    const [settings, setSettings] = useState(() => {
        // Khởi tạo state, kết hợp giá trị từ prop.value với cấu trúc mặc định
        const defaultSettings = getDefaultSettings(catalogs);
        if (value && Object.keys(value).length > 0) {
            return {
                ...defaultSettings,
                ...value,
                menu_visibility: {...defaultSettings.menu_visibility, ...value.menu_visibility},
                // Đảm bảo catalog_menu_items được lấy từ value, nếu không có thì dùng default
                catalog_menu_items: value.catalog_menu_items || defaultSettings.catalog_menu_items,
            };
        }
        return defaultSettings;
    });

    // Đồng bộ hóa state nội bộ khi prop 'value' hoặc 'catalogs' thay đổi
    useEffect(() => {
        // 1. Tạo cấu hình mặc định mới dựa trên danh sách catalogs mới nhất
        const defaultSettings = getDefaultSettings(catalogs);

        let newSettings = defaultSettings;

        if (value && Object.keys(value).length > 0) {
            // Bắt đầu với các giá trị mặc định mới (dùng catalogs mới)
            newSettings = {
                ...defaultSettings,
                ...value, // Ghi đè bằng dữ liệu đã lưu
                menu_visibility: {...defaultSettings.menu_visibility, ...value.menu_visibility},
            };

            // 2. Xử lý catalog_menu_items: Merge danh sách catalogs mới với trạng thái isVisible đã lưu trong 'value'.
            const savedCatalogsMap = new Map(value.catalog_menu_items?.map(c => [c.id, c]) || []);

            const mergedCatalogs = catalogs.map(newCat => {
                if (savedCatalogsMap.has(newCat.id)) {
                    // Giữ lại trạng thái isVisible (và name, id) đã lưu
                    const oldValues = savedCatalogsMap.get(newCat.id);
                    return {...oldValues, ...newCat}
                } else {
                    // Đây là catalog mới, mặc định ẩn (theo getDefaultSettings)
                    return {...newCat, isVisible: false};
                }
            });

            newSettings.catalog_menu_items = mergedCatalogs;
        }

        // Cập nhật state nếu có sự khác biệt
        setSettings(newSettings);

    }, [value, catalogs]); // Dependency array bao gồm value và catalogs

    // Callback để cập nhật state và gọi onChange
    const updateSettings = useCallback((newPartialSettings) => {
        setSettings(prev => {
            const newSettings = {...prev, ...newPartialSettings};
            // Gọi onChange để truyền giá trị JSON đầy đủ ra ngoài form/parent
            if (handleChange) {
                handleChange(newSettings);
            }
            return newSettings;
        });
    }, [handleChange]);

    // Xử lý thay đổi loại Layout (HERO/BANNER)
    const handleLayoutTypeChange = (e) => {
        updateSettings({layout_type: e.target.value});
    };

    // Xử lý thay đổi trạng thái hiển thị của các mục menu cố định
    const handleFixedMenuVisibilityChange = (key, isChecked) => {
        updateSettings({
            menu_visibility: {
                ...settings.menu_visibility,
                [key]: isChecked,
            },
        });
    };

    // Xử lý thay đổi danh mục sản phẩm tùy chọn
    const handleCatalogVisibilityChange = (catalogId, isChecked) => {
        const newCatalogs = settings.catalog_menu_items.map(item =>
            item.id === catalogId ? {...item, isVisible: isChecked} : item
        );
        updateSettings({catalog_menu_items: newCatalogs});
    };

    // Xử lý xóa danh mục tùy chọn (chỉ áp dụng cho các danh mục do người dùng thêm)
    const handleDeleteCatalog = (catalogId) => {
        const newCatalogs = settings.catalog_menu_items.filter(item => item.id !== catalogId);
        updateSettings({catalog_menu_items: newCatalogs});
    };

    // Xử lý thêm danh mục mới (mock)
    const handleAddCatalog = () => {
        const newId = `cat_${Date.now()}`;
        const newCatalog = {
            id: newId,
            name: `new categories option #${settings.catalog_menu_items.length + 1}`,
            isVisible: true, // Mặc định hiển thị khi thêm mới
        };
        updateSettings({catalog_menu_items: [...settings.catalog_menu_items, newCatalog]});
    };


    // Phân tách các danh mục tùy chọn (Đang hiển thị / Đã ẩn) để dễ quản lý
    const activeCatalogs = useMemo(() =>
            settings.catalog_menu_items.filter(item => item.isVisible),
        [settings.catalog_menu_items]
    );
    const inactiveCatalogs = useMemo(() =>
            settings.catalog_menu_items.filter(item => !item.isVisible),
        [settings.catalog_menu_items]
    );

    return (
        <div className="min-h-screen">
            <div className="mb-4 font-bold text-base text-gray-700 tracking-wide">
                {t('display & layout configs')}
            </div>

            <Space direction="vertical" size="large" className="w-full">
                {/* ======================= 1. Loại Layout ======================= */}
                <div className="border border-color-50 shadow-lg hover:shadow-xl rounded-xl transition-all">
                    <div
                        className="mb-2 px-3 py-2 border-b border-color-50 font-semibold text-lg text-indigo-600 tracking-wide">
                        {`1. ${t('home layout')}`}
                    </div>
                    <div className="p-3 flex flex-col gap-6">
                        <div className="flex flex-col gap-3">
                            <label className="text-sm font-medium text-color-200 tracking-wider">
                                {t('choose the main display style for your shop header section')}
                            </label>
                            <Radio.Group
                                onChange={handleLayoutTypeChange} value={settings.layout_type}
                                className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 custom-radio-with-gap">
                                <Radio.Button value="BANNER" className="flex-1 p-3 text-center hover:shadow-md cursor-pointer">
                                    <div className="font-semibold text-base tracking-wide">
                                        {t('BANNER (Classic)')}
                                    </div>
                                    <span className="text-sm text-color-300 tracking-wider leading-tight">
                                        {t('display a large banner strip at the top')}
                                    </span>
                                </Radio.Button>
                                <Radio.Button value="HERO" className="flex-1 p-3 text-center hover:shadow-md cursor-pointer">
                                    <div className="font-semibold text-base tracking-wide">
                                        {t('HERO (Featured)')}
                                    </div>
                                    <span className="text-sm text-color-300 tracking-wider leading-tight">
                                        {t('display a featured product or information section that occupies most of the screen')}
                                    </span>
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-color-200 tracking-wider">
                                    {t('image')}
                                </label>
                                <span className="text-xs text-color-300 italic tracking-wider">
                                    {`* ${t('upload photo descriptions')}`}
                                </span>
                            </div>
                            <ShopLayoutPhotos photos={photos} shop_id={shop_id}/>
                        </div>
                    </div>
                </div>

                {/* ======================= 2. Cài đặt Menu Hiển Thị ======================= */}
                <div className="border border-color-50 shadow-lg hover:shadow-xl rounded-xl transition-all">
                    <div
                        className="mb-2 px-3 py-2 border-b border-color-50 font-semibold text-lg text-indigo-600 tracking-wide">
                        {`2. ${t('navigation menu settings')}`}
                    </div>
                    <Collapse
                        defaultActiveKey={['fixed_items']}
                        expandIconPosition="right"
                        className="m-3 rounded-lg overflow-hidden">
                        {/* --- Menu Items Cố Định --- */}
                        <Collapse.Panel
                            key="fixed_items"
                            header={
                                <span
                                    className="font-bold text-gray-800 tracking-wide">{`2.1. ${t('Basic menus (Recent, Discounts)')}`}</span>
                            }>
                            <Space direction="vertical" className="w-full px-3">
                                <ul style={{paddingLeft: 20, listStyleType: 'disc'}} className="flex flex-col gap-2">
                                    {[
                                        {key: 'recent', label: 'recent products'},
                                        {key: 'discount', label: 'discount products'},
                                        {key: 'upcoming', label: 'upcoming products'},
                                    ].map((item, index) => (
                                        <li key={index} className="text-color-500 leading-tight">
                                            <div key={item.key} className="flex items-center gap-3">
                                                <div className="flex-1 font-medium text-gray-700">
                                                    {t(item.label)}
                                                </div>
                                                <Switch
                                                    checked={settings.menu_visibility[item.key]}
                                                    onChange={(checked) => handleFixedMenuVisibilityChange(item.key, checked)}
                                                    checkedChildren={t('visible')}
                                                    unCheckedChildren={<span
                                                        className="text-color-300">{t('invisible')}</span>}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                    {/* Mục Mới Nhất có thêm cài đặt Limit */}
                                    <div className="border-t pt-4 mt-2">
                                        <li className="text-color-500 leading-tight">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 font-medium text-gray-700">
                                                    {t('newest products')}
                                                </div>
                                                <Switch
                                                    checked={settings.show_newest}
                                                    onChange={(checked) => updateSettings({
                                                        show_newest: checked,
                                                        menu_visibility: {...settings.menu_visibility, newest: checked}
                                                    })}
                                                    checkedChildren={t('visible')}
                                                    unCheckedChildren={<span
                                                        className="text-color-300">{t('invisible')}</span>}
                                                />
                                            </div>
                                        </li>
                                        {settings.show_newest && (
                                            <div className="mt-2 flex items-center gap-3">
                                                <div className="text-color-300 tracking-wider whitespace-no-wrap">
                                                    {`${t('Limit (Number of Products)')}: `}
                                                </div>
                                                <InputNumber
                                                    min={1}
                                                    max={50}
                                                    value={settings.newest_limit}
                                                    onChange={(value) => updateSettings({newest_limit: value})}
                                                    className="flex-1"
                                                    placeholder="quantity"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </ul>
                            </Space>
                        </Collapse.Panel>

                        {/* --- Menu Items Tùy Chọn (Product Catalogs) --- */}
                        <Collapse.Panel
                            key="catalog_items"
                            header={
                                <span
                                    className="font-bold text-gray-800 tracking-wide">{`2.2. ${t('Product categories on the Menu')}`}</span>
                            }>
                            <Space direction="vertical" className="w-full px-3">
                                {activeCatalogs.length > 0 && (
                                    <div className="font-semibold text-indigo-600 tracking-wider">
                                        {`${activeCatalogs.length} ${t('displayed categories')}:`}
                                    </div>
                                )}
                                <ul style={{paddingLeft: 20, listStyleType: 'disc'}} className="flex flex-col gap-2">
                                    {activeCatalogs.map((item, index) => (
                                        <li key={index} className="text-color-500 leading-tight">
                                            <div key={item.id} className="flex items-center gap-3">
                                                <div className="flex-1 font-medium text-gray-700">
                                                    {item.title}
                                                </div>
                                                <Space size="small">
                                                    <Button
                                                        type="link"
                                                        danger
                                                        icon={<BsTrash/>}
                                                        onClick={() => handleDeleteCatalog(item.id)}
                                                        title="delete"
                                                    />
                                                    <Switch
                                                        size="small"
                                                        checked={item.isVisible}
                                                        onChange={(checked) => handleCatalogVisibilityChange(item.id, checked)}
                                                    />
                                                </Space>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <Divider className="my-2"/>
                                {inactiveCatalogs.length > 0 && (
                                    <div className="font-semibold text-color-400 tracking-wider">
                                        {`${inactiveCatalogs.length} ${t('hidden categories')}:`}
                                    </div>
                                )}
                                <ul style={{paddingLeft: 20, listStyleType: 'disc'}} className="flex flex-col gap-2">
                                    {inactiveCatalogs.map((item, index) => (
                                        <li key={index} className="text-color-500 leading-tight">
                                            <div key={item.id} className="flex items-center gap-3">
                                                <div className="flex-1 font-medium text-gray-700">
                                                    {item.title}
                                                </div>
                                                <Switch
                                                    checked={item.isVisible}
                                                    onChange={(checked) => handleCatalogVisibilityChange(item.id, checked)}
                                                    checkedChildren={t('visible')}
                                                    unCheckedChildren={<span
                                                        className="text-color-300">{t('invisible')}</span>}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    block type="dashed"
                                    onClick={handleAddCatalog}
                                    icon={<PlusOutlined/>}
                                    className="mt-4 border-dashed border-gray-400 text-gray-600 hover:border-blue-500 hover:text-blue-500">
                                    {t('add categories option')}
                                </Button>
                            </Space>
                        </Collapse.Panel>

                    </Collapse>
                </div>
            </Space>

            {/* --- Hiển thị JSON kết quả để minh họa việc lưu trữ --- */}
            <ForDevelop>
                <div className="mt-12">
                    <pre className="text-xs p-3 bg-gray-100 rounded-lg overflow-x-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(settings, null, 2)}
                    </pre>
                </div>
            </ForDevelop>
        </div>
    );
};

const ShopLayoutImageUpload = ({photos, shop_id, fileList, setFileList, onChange, children}) => {
    const t = useTranslate();

    return (
        <Upload
            accept=".jpg,.jpeg,.png,.webp"
            // multiple={true}
            listType="picture"
            fileList={fileList}
            onChange={onChange}
            customRequest={async ({onSuccess, ...args}) => {
                const response = await callApi({
                    apiInfo: image_directUpload_Api,
                    values: {
                        'photos[]': args.file,
                    },
                    query: bindQueryParams([
                        {type: 'shop'},
                        {id: shop_id},
                    ]),
                })
                const newFileList = _.map(fileList, (item) =>
                    item.uid === args?.file?.uid
                        ? {
                            ...args?.file,
                            id: _.get(response, 'data.ids.0'),
                            status: 'done',
                            url: (_.get(response, 'data.urls.0') || '').startsWith('http')
                                ? _.get(response, 'data.urls.0')
                                : `${STORAGE_URL}/${_.get(response, 'data.urls.0')}`,
                        }
                        : item
                )
                setFileList(newFileList)
            }}
            onRemove={async (file) => {
                const newFileList = fileList.filter((item) => item.uid !== file.uid)
                setFileList(newFileList)
                const response = await callApi({
                    apiInfo: image_directDestroy_Api,
                    query: bindQueryParam({id: file.id}),
                })
            }}
            showUploadList={false}>
            {children}
        </Upload>
    )
}

const ShopLayoutPhotos = ({photos, shop_id}) => {
    const t = useTranslate()
    const [fileList, setFileList] = useState(
        photos?.map((e) => ({
            ...e,
            uid: e.id,
            name: e.id,
            url: e.path,
            status: 'done',
        }))
    )

    const _height = 200

    const {
        onDirectUpload,
        onDirectDelete,
        onMultiDirectDelete,
    } = useImageActions({
        notifyListeners: {
            onCreated: (result, data) => {

            },
            onDeleted: (result, data) => {

            },
            onMultiDeleted: (result, data) => {

            },
        },
    })

    const onChange = ({fileList: newFileList}) => {
        setFileList(newFileList);
    };

    const [selectedFiles, setSelectedFiles] = useState([]);

    // select all ảnh
    const handleSelectAll = () => {
        setSelectedFiles(fileList.map((file) => file.uid));
    };

    // deselect all ảnh
    const handleDeselectAll = () => {
        setSelectedFiles([]);
    };

    // Chọn ảnh để xóa nhiều ảnh cùng lúc
    const handleSelectFile = (file, checked) => {
        setSelectedFiles((prev) =>
            checked ? [...prev, file.uid] : prev.filter((id) => id !== file.uid)
        );
    };

    // Xóa nhiều ảnh cùng lúc
    const handleRemoveSelected = () => {
        showDeleteConfirmDialog({
            translate: t,
            title: 'delete photos',
            message: `${t('are you sure you want to delete photos')} ?`,
            onOk: () => {
                onMultiDirectDelete({ids: selectedFiles.join(",")})
                setFileList((prev) => prev.filter((file) => !selectedFiles.includes(file.uid)));
                setSelectedFiles([]); // Reset danh sách đã chọn
            },
        })
    };

    return (
        <>
            <div className="pb-3 border-b border-color-50 flex items-center gap-2 overflow-x-auto">
                <ShopLayoutImageUpload {...{photos, shop_id, fileList, setFileList, onChange}}>
                    <CustomButton
                        ift="add"
                        color="primary"
                        variant="solid"
                        label="add photo"
                        textSize="text-xs"
                        style={{height: 28}}
                    />
                </ShopLayoutImageUpload>
                <div className="flex-1"/>
                <CustomButton
                    ift="select-all"
                    color={selectedFiles.length === fileList.length ? "default" : "primary"}
                    variant="filled"
                    label="select all"
                    textSize="text-xs"
                    onClick={handleSelectAll}
                    style={{height: 28}}
                />
                <CustomButton
                    ift="deselect-all"
                    color={selectedFiles.length === 0 ? "default" : "primary"}
                    variant="filled"
                    label="deselect all"
                    textSize="text-xs"
                    onClick={handleDeselectAll}
                    style={{height: 28}}
                />
                {renderIf(
                    selectedFiles.length > 0,
                    <CustomButton
                        ift="delete-all"
                        color="primary"
                        variant="text"
                        label={
                            selectedFiles.length === fileList.length ? 'delete all' :
                                selectedFiles.length > 0 ? t("delete {n} selected photos", {n: selectedFiles.length}) : 'delete'
                        }
                        textSize="text-xs"
                        onClick={handleRemoveSelected}
                        style={{height: 28}}
                    />
                )}
            </div>
            <Image.PreviewGroup>
                <div className="flex flex-col space-y-6">
                    {fileList.map((file, index) => {
                        return (
                            <div key={index} className="relative">
                                <Image
                                    loading="lazy"
                                    key={index}
                                    src={file.url || file.preview}
                                    style={{minHeight: _height}}
                                    wrapperClassName={classNames("rounded-lg", selectedFiles.includes(file.uid) && 'border border-primary')}
                                    className="object-cover rounded-lg cursor-pointer"
                                    preview={{mask: renderMaskImage()}}
                                />
                                <div className="absolute top-5 left-5">
                                    <Checkbox
                                        className="flex cursor-pointer"
                                        onChange={(e) => handleSelectFile(file, e.target.checked)}
                                        checked={selectedFiles.includes(file.uid)}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </Image.PreviewGroup>
        </>
    )
}

export const formSchema = {
    status: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        itemClassNames: ['col-span-1 md:col-span-2', 'col-span-1'],
        children: [
            {
                label: 'status',
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="w-full custom-radio-button"
                        />
                    )
                },
            },
            {
                name: 'active',
                label: 'active',
                component: createControlledFormField({
                    Component: BooleanPicker,
                }),
            },
        ],
    },
    avatar: {
        children: [
            {
                name: 'avatar',
                component: createControlledFormField({
                    withValues: ({avatar_preview}) => ({
                        value: avatar_preview,
                        width: 150,
                        height: 150,
                        icon: <BsImage size={70} style={{color: '#eaeaea'}}/>,
                    }),
                    Component: AvatarUploader,
                }),
            },
        ],
    },
    shop_layout_configs: {
        // title: 'shop layout configs',
        children: [
            {
                name: 'shop_layout_configs',
                component: createControlledFormField({
                    className: 'w-full',
                    Component: ShopLayoutSettings,
                    withProps: ({name, value, onChange, form}) => ({
                        value: value,
                        handleChange: (_value) => {
                            onChange(createValue(name, _value))
                        },
                    }),
                    withValues: ({resetKey, catalogs, photos, id}) => ({
                        resetKey,
                        catalogs,
                        shop_id: id,
                        photos
                    }),
                }),
            },
        ],
    },
    title: {
        title: 'shop name',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'shop name',
                    showCount: true,
                    maxLength: 120,
                    className: 'rounded-md',
                }),
            },
        ],
    },
    description: {
        title: 'short description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 5,
                    showCount: true,
                    maxLength: 500,
                    placeholder: 'short description',
                    Component: TextArea,
                    className: 'scrollBarThin',
                }),
            },
        ],
    },
    idname: {
        title: 'url',
        children: {
            name: 'idname',
            component: compose(
                mapProps(({onChange, value}) => ({
                    onChange,
                    value,
                    name: 'idname',
                    addonBefore: 'https://.../course/',
                    placeholder: 'please input name!',
                    type: 'text',
                    trigger: ['focus'],
                    title: ROOT_URL + '/course/' + value,
                    placement: 'topLeft',
                })),
                pure
            )(nest(TooltipApp, Input)),
        },
    },
    privacy: {
        title: 'privacy',
        children: {
            name: 'privacy',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 2,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 2 : 1))
                    },
                    title: translate('privacy'),
                    description: translate("This parameter allows whether non-member users are restricted from access or not."),
                    className: "my-3 p-2 border border-dashed border-primary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },
    about: {
        title: 'about shop',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    className: 'background border rounded-lg',
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
}

export const ShopFormCreate = ({
                                   owner_id,
                                   owner_type,
                                   onCancel = NullAsDefault,
                                   onSubmit = NullAsDefault,
                               }) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}

        const {title} = values ?? {}

        if (!title) {
            errors.title = t('required field')
        }

        return errors
    }

    const initialValues = useMemo(() => {
        return {
            owner_id,
            owner_type,
            title: undefined,
            description: undefined,
        }
    }, [owner_id, owner_type])

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    ...rest
                } = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                <ActionModalWrapper
                    action={
                        <FormActionGroups
                            submitProps={{
                                label: 'create',
                            }}
                        />
                    }>
                    <div className="flex flex-col gap-3">
                        <Pure>
                            <FieldsFactory
                                formSchema={[
                                    formSchema.title,
                                    formSchema.description,
                                    formSchema.about,
                                ]}
                            />
                        </Pure>

                    </div>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

const getLeafNodes = (data) => {
    return _.flatMap(data, item => {
        if (_.isEmpty(item.children)) {
            return [item]; // leaf
        }
        return getLeafNodes(item.children); // duyệt xuống sâu hơn
    });
}

export const ShopFormEdit = ({
                                 owner_id,
                                 owner_type,
                                 onCancel = NullAsDefault,
                                 onSubmit = NullAsDefault,
                                 initialValues = {},
                             }) => {
    const t = useTranslate()
    const [current, setCurrent] = useState('overview')
    const catalogs = getLeafNodes(_.get(initialValues, 'product_catalogs', []));

    const _initialValues = useMemo(() => {
        const {
            about,
            avatar,
            ...rest
        } = initialValues ?? {}
        const status_options = [
            StatusOptions.normal,
            StatusOptions.closed,
        ].map(deepTranslate(t))
        return {
            ...rest,
            catalogs,
            owner_id,
            owner_type,
            status_options,
            about: convertFromString(about),
            avatar_preview: avatar,
        }
    }, [initialValues, owner_id, owner_type])

    const validate = (values) => {
        let errors = {}
        const {title} = values ?? {}
        if (!title) {
            errors.title = t('required field')
        }
        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    status_options,
                    shop_layout_configs,
                    ...rest
                } = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            shop_layout_configs: shop_layout_configs ? JSON.stringify(shop_layout_configs) : undefined,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                <ErrorSummaries/>
                <ActionModalWrapper
                    tab={current}
                    action={<FormActionBar submitText="save"/>}
                    onTabChange={(key) => {
                        setCurrent(key)
                    }}
                    tabs={[
                        {
                            key: 'overview',
                            label: 'overview',
                            component: ({child}) => child,
                        },
                        {
                            key: 'about',
                            label: 'about',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.about]}/>
                            ),
                        },
                        {
                            key: 'shop_layout_configs',
                            label: 'shop layout configs',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.shop_layout_configs]}/>
                            ),
                        },
                    ]}>
                    <Pure>
                        <FieldsFactory
                            formSchema={[
                                formSchema.title,
                                formSchema.description,
                                formSchema.avatar,
                            ]}
                        />
                    </Pure>


                    <Pure>
                        <div className="flex flex-col gap-3">
                            <HeaderWithIconLine
                                className="pt-2 mt-3"
                                renderTitle={() => (
                                    <div className="flex items-center gap-2">
                                        <BsShieldFillExclamation size={16} className="text-color-500"/>
                                        <span
                                            className="font-semibold text-base text-color-400 uppercase tracking-wider">
                        {t('privacy')}
                    </span>
                                    </div>
                                )}
                            />
                            <div className="ml-2 flex flex-col gap-3">
                                <FieldsFactory
                                    formSchema={[
                                        formSchema.status,
                                        formSchema.privacy,
                                    ]}
                                />
                            </div>
                        </div>
                    </Pure>

                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}
